// Single source of truth for every finance product Elin's introduces.
// Each product belongs to a family (used for the staff picker) and maps to a
// product-specific "block" key (used by applicationSchemas.js to decide which
// extra section to insert in the application form).
//
// Keep this file additive — never rename or delete a key, because form
// submissions and customer records in production reference these strings.

export const PRODUCT_FAMILIES = [
  {
    id: "residential",
    label: "Residential",
    sub: "Buying or owning your own home",
  },
  {
    id: "refinance",
    label: "Refinance & Further Borrowing",
    sub: "Changing or topping up an existing mortgage",
  },
  {
    id: "btl",
    label: "Buy-to-let & Portfolio",
    sub: "Mortgages on rental properties",
  },
  {
    id: "bridging_dev",
    label: "Bridging & Development",
    sub: "Short-term and project finance",
  },
  {
    id: "commercial",
    label: "Commercial",
    sub: "Trading premises and mixed-use property",
  },
  {
    id: "business",
    label: "Business Finance",
    sub: "Funding for a limited company, sole trader or partnership",
  },
  {
    id: "personal",
    label: "Personal",
    sub: "Everyday borrowing for an individual",
  },
];

// Products. `block` tells the schema which product-specific section to insert.
// `label` is the human name used across the UI, PDF title and attached filename.
// `legacy` keys ("mortgage" and "remortgage") stay first so existing records
// render identically.
export const PRODUCTS = [
  // ── Residential ────────────────────────────────────────────────────────
  { key: "mortgage",            label: "Residential mortgage (purchase)",      family: "residential", block: "property_purchase",      short: "Mortgage" },
  { key: "first_time_buyer",    label: "First-time buyer mortgage",            family: "residential", block: "property_purchase_ftb",  short: "FTB mortgage" },
  { key: "shared_ownership",    label: "Shared ownership",                     family: "residential", block: "shared_ownership",       short: "Shared ownership" },
  { key: "self_build",          label: "Self-build mortgage",                  family: "residential", block: "self_build",             short: "Self-build" },
  { key: "equity_release",      label: "Equity release",                       family: "residential", block: "equity_release",         short: "Equity release" },

  // ── Refinance & further borrowing ─────────────────────────────────────
  { key: "remortgage",          label: "Remortgage",                           family: "refinance",   block: "remortgage",             short: "Remortgage" },
  { key: "further_advance",     label: "Further advance",                      family: "refinance",   block: "further_advance",        short: "Further advance" },
  { key: "let_to_buy",          label: "Let-to-buy",                           family: "refinance",   block: "let_to_buy",             short: "Let-to-buy" },
  { key: "second_charge",       label: "Second charge loan",                   family: "refinance",   block: "second_charge",          short: "Second charge" },

  // ── Buy-to-let & portfolio ────────────────────────────────────────────
  { key: "buy_to_let",          label: "Buy-to-let mortgage",                  family: "btl",         block: "btl",                    short: "Buy-to-let" },
  { key: "ltd_co_btl",          label: "Limited company buy-to-let",           family: "btl",         block: "ltd_co_btl",             short: "Ltd Co BTL" },
  { key: "holiday_let",         label: "Holiday let mortgage",                 family: "btl",         block: "holiday_let",            short: "Holiday let" },
  { key: "hmo_multi_unit",      label: "HMO & multi-unit mortgage",            family: "btl",         block: "hmo_multi_unit",         short: "HMO/MUB" },

  // ── Bridging & Development ────────────────────────────────────────────
  { key: "bridging_regulated",  label: "Bridging loan (regulated)",            family: "bridging_dev", block: "bridging",              short: "Bridging (reg)" },
  { key: "bridging_unregulated", label: "Bridging loan (unregulated)",         family: "bridging_dev", block: "bridging",              short: "Bridging (unreg)" },
  { key: "auction_finance",     label: "Auction finance",                      family: "bridging_dev", block: "auction_finance",        short: "Auction finance" },
  { key: "development_finance", label: "Development finance",                  family: "bridging_dev", block: "development",            short: "Development finance" },
  { key: "development_exit",    label: "Development exit finance",             family: "bridging_dev", block: "development_exit",       short: "Dev exit" },
  { key: "refurb_light",        label: "Refurbishment finance (light)",        family: "bridging_dev", block: "refurb",                 short: "Light refurb" },
  { key: "refurb_heavy",        label: "Refurbishment finance (heavy)",        family: "bridging_dev", block: "refurb",                 short: "Heavy refurb" },

  // ── Commercial ────────────────────────────────────────────────────────
  { key: "commercial_mortgage", label: "Commercial mortgage",                  family: "commercial",  block: "commercial_property",    short: "Commercial mortgage" },
  { key: "semi_commercial",     label: "Semi-commercial mortgage",             family: "commercial",  block: "semi_commercial",        short: "Semi-commercial" },

  // ── Business ──────────────────────────────────────────────────────────
  { key: "business_loan",       label: "Business loan",                        family: "business",    block: "business_loan",          short: "Business loan" },
  { key: "asset_finance",       label: "Asset finance (equipment & vehicles)", family: "business",    block: "asset_finance",          short: "Asset finance" },
  { key: "invoice_finance",     label: "Invoice finance",                      family: "business",    block: "invoice_finance",        short: "Invoice finance" },
  { key: "merchant_cash_advance", label: "Merchant cash advance",              family: "business",    block: "mca",                    short: "MCA" },
  { key: "working_capital",     label: "Working capital or overdraft",         family: "business",    block: "working_capital",        short: "Working capital" },
  { key: "commercial_vehicle",  label: "Commercial vehicle finance",           family: "business",    block: "commercial_vehicle",     short: "Commercial vehicle" },

  // ── Personal ──────────────────────────────────────────────────────────
  { key: "car_finance_hp",      label: "Car finance (HP)",                     family: "personal",    block: "car_finance",            short: "Car finance (HP)" },
  { key: "car_finance_pcp",     label: "Car finance (PCP)",                    family: "personal",    block: "car_finance",            short: "Car finance (PCP)" },
  { key: "personal_loan",       label: "Personal loan",                        family: "personal",    block: "personal_loan",          short: "Personal loan" },
  { key: "debt_consolidation",  label: "Debt consolidation",                   family: "personal",    block: "debt_consolidation",     short: "Debt consolidation" },
  { key: "credit_card",         label: "Credit card & other credit",           family: "personal",    block: "credit_card",            short: "Credit card" },
];

export const PRODUCT_KEYS = PRODUCTS.map((p) => p.key);

export const findProduct = (key) =>
  PRODUCTS.find((p) => p.key === key) ||
  PRODUCTS.find((p) => p.key === "mortgage");

export const productLabel = (key) => findProduct(key).label;
export const productShort = (key) => findProduct(key).short;
export const productFamily = (key) => findProduct(key).family;
export const productBlock  = (key) => findProduct(key).block;

export const productsByFamily = () => {
  const map = Object.fromEntries(PRODUCT_FAMILIES.map((f) => [f.id, []]));
  for (const p of PRODUCTS) {
    if (!map[p.family]) map[p.family] = [];
    map[p.family].push(p);
  }
  return map;
};

// Products that revolve around a specific property (used to decide whether
// the "Estate Agent, Solicitor & Bank Details" section applies).
export const PROPERTY_FAMILIES = new Set(["residential", "refinance", "btl", "bridging_dev", "commercial"]);
export const isPropertyProduct = (key) => PROPERTY_FAMILIES.has(productFamily(key));
